import { models } from "powerbi-client";
import type { IReportEmbedConfiguration } from "powerbi-client-react";
import { PowerBIEmbed } from "powerbi-client-react";
import React, { useEffect, useState } from "react";

import { api } from "@/api";
import type { PowerBiEmbedConfig } from "@/types/powerbi";

export interface PowerBIReportProps {
  /** Power BI workspace group ID */
  groupId: string;
  /** Power BI report ID */
  reportId: string;
  /** Report display name for accessibility */
  reportName?: string;
  /** Custom className for the container */
  className?: string;
  /** Callback when embed loads successfully */
  onLoad?: () => void;
  /** Callback when embed encounters an error */
  onError?: (error: Error) => void;
  /** Custom height (default: 600px) */
  height?: string | number;
  /** Custom width (default: 100%) */
  width?: string | number;
}

/**
 * PowerBIReport Component
 *
 * Embeds a Power BI report using backend-provided configuration.
 *
 * Flow:
 * 1. Component receives groupId and reportId
 * 2. On mount & when reportId changes, fetches embed config from backend
 * 3. Backend returns embedUrl, embedToken, and expiration
 * 4. Renders PowerBIEmbed with the configuration
 *
 * Security:
 * - Backend handles token generation and expiration
 * - Frontend never touches Power BI service principal credentials
 * - Tokens are short-lived (30 min)
 * - User permissions validated on backend before issuing tokens
 */
const PowerBIReport: React.FC<PowerBIReportProps> = ({
  groupId,
  reportId,
  reportName = "Power BI Report",
  className,
  onLoad,
  onError,
  height = 600,
  width = "100%",
}) => {
  const [embedConfig, setEmbedConfig] = useState<PowerBiEmbedConfig | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  // Normalize height to string with unit
  const normalizedHeight = typeof height === "number" ? `${height}px` : height;
  const normalizedWidth = typeof width === "number" ? `${width}px` : width;

  // Fetch embed configuration when groupId or reportId changes
  useEffect(() => {
    let isMounted = true;

    const fetchEmbedConfig = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const config = await api.getPowerBiEmbedConfig(groupId, reportId);
        if (isMounted) {
          setEmbedConfig(config);
        }
      } catch (err: any) {
        if (isMounted) {
          const error = new Error(err?.message || "Failed to fetch embed configuration");
          setError(error);
          onError?.(error);
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    void fetchEmbedConfig();

    return () => {
      isMounted = false;
    };
  }, [groupId, reportId, onError]);

  // Error state - failed to fetch embed configuration
  if (error) {
    return (
      <div
        className={`flex items-center justify-center rounded-lg border border-red-200 bg-red-50 p-8 ${className || ""}`}
        style={{ height: normalizedHeight, width: normalizedWidth }}
      >
        <div className="text-center space-y-2">
          <h3 className="text-base font-semibold text-red-900">Failed to Load Report</h3>
          <p className="text-sm text-red-700">{error.message}</p>
          <p className="text-xs text-red-600 pt-2">
            Report: {reportId} | Group: {groupId}
          </p>
        </div>
      </div>
    );
  }

  // Loading state
  if (isLoading) {
    return (
      <div
        className={`flex items-center justify-center rounded-lg border border-gray-200 bg-gray-50 ${className || ""}`}
        style={{ height: normalizedHeight, width: normalizedWidth }}
      >
        <div className="space-y-4 text-center">
          <div className="flex justify-center">
            <div className="h-8 w-8 animate-spin rounded-full border-4 border-gray-200 border-t-blue-600" />
          </div>
          <div>
            <p className="text-sm font-medium text-gray-900">Loading report...</p>
            <p className="mt-1 text-xs text-gray-500">{reportName}</p>
          </div>
        </div>
      </div>
    );
  }

  // No embed config - should not reach here
  if (!embedConfig) {
    return (
      <div
        className={`flex items-center justify-center rounded-lg border border-yellow-200 bg-yellow-50 p-8 ${className || ""}`}
        style={{ height: normalizedHeight, width: normalizedWidth }}
      >
        <div className="text-center space-y-2">
          <p className="text-sm font-medium text-yellow-900">No embed configuration</p>
          <p className="text-xs text-yellow-700">Unable to display report at this time</p>
        </div>
      </div>
    );
  }

  // Build embed configuration for powerbi-client-react
  const reportEmbedConfig: IReportEmbedConfiguration = {
    type: "report",
    id: embedConfig.reportId,
    embedUrl: embedConfig.embedUrl,
    accessToken: embedConfig.embedToken,
    tokenType: models.TokenType.Embed,
    permissions: models.Permissions.Read,
    settings: {
      paginatedReportFormatSettings: [
        {
          locale: "en-US",
          numericDecimalSeparator: ".",
          numericGroupingSeparator: ",",
        },
      ],
    },
  };

  return (
    <div
      className={`relative rounded-lg overflow-hidden border border-gray-200 ${className || ""}`}
      style={{ height: normalizedHeight, width: normalizedWidth }}
    >
      <PowerBIEmbed
        embedConfig={reportEmbedConfig}
        cssClassName="w-full h-full"
        onLoad={onLoad}
        onError={(error: any) => {
          onError?.(new Error(error?.message || "Power BI embedding failed"));
        }}
      />
    </div>
  );
};

export default PowerBIReport;
